import {Box, Button, IconButton, InputAdornment, TextField, TextFieldProps} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment, {Moment} from "moment";
import {makeStyles} from "@mui/styles";
import Calendar, {CalendarProps} from "react-calendar";
import {PortTheme} from "~/ui/theme";
import {CalendarIcon, LinaArrowRight} from "~/ui/icons";
import {BottomSheet} from "~/apps/core/ui/BottomSheet";
import {useBooleanHistoryState} from "~/apps/core/utils";


export type DateFieldProps = Omit<TextFieldProps, "value" | "defaultValue" | "onChange"> & {
    value?: Date
    onChange?: (value?: Date | [Date, Date]) => void
    range?: boolean
    maxDate?: Date
}


const useStyles = makeStyles<PortTheme>((theme) => ({
    root: {
        //заплатка в FF
        '@supports (-moz-appearance:none)': {
            "&::before": {
                content: '""',
                position: 'absolute',
                bottom: '20px',
                right: '60px',
                width: '30px',
                height: '1.4375em',
                background: '#F7F7FA',
                zIndex: 1,
            },
        },
        "& input": {
            appearance: "none",
            "&[type=\"date\"]::-webkit-inner-spin-button, &[type=\"date\"]::-webkit-calendar-picker-indicator, &[type=\"date\"]::-webkit-clear-button": {
                display: "none",
                appearance: "none",
                "-webkit-appearance": "none",
            }
        }
    },
    container: {
        maxWidth: '375px',
        height: '480px',
        display: 'flex',
        flexDirection: 'column',
        '@media (max-height: 580px) and (max-width: 360px)': {
            height: '431px'
        }
    },
    calendar: {
        maxWidth: '375px',
        background: '#FFFFFF',
        borderRadius: '14px 14px 0px 0px',
        "& button": {
            backgroundColor: 'transparent',
            padding: '0px',
            border: 'none',
        },
        "& .react-calendar__navigation": {
            display: "flex",
            justifyContent: "space-between",
            padding: '0px 16px',
            margin: '10px 0',
            height: '24px',
            "& button:first-child, & button:last-child": {
                display: "none",
            },
            "& .react-calendar__navigation__prev-button": {
                transform: 'rotate(180deg)',
            },
            '& .react-calendar__navigation__label': {
                fontWeight: 300,
                fontSize: '20px',
                lineHeight: '24px',
                letterSpacing: '0.38px',
                color: '#081F32',
                textTransform: 'capitalize',
                '@media (max-height: 580px)  and (max-width: 360px)': {
                    fontSize: '16px',
                    lineHeight: '20px',
                }
            }
        },
        "& .react-calendar__month-view__weekdays": {
            height: '20px',
            backgroundColor: '#F0F4F9',
            textAlign: 'center',
            padding: '0px 16px',
            '& abbr': {
                textDecoration: 'none',
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: "20px",
                letterSpacing: "0.002em",
                color: theme.palette.additionalPrimaryDisabled.main,

            }
        },
        "& .react-calendar__month-view__days": {
            padding: '0px 16px',
        },

    },
    tileMonth: {
        fontSize: "20px",
        fontWeight: 600,
        lineHeight: "25px",
        letterSpacing: "0.002em",
        color: '#081F32',
        cursor: "pointer",
        height: 'min(44px, calc((100vw - 32px - 30px) / 7))',
        width: 'min(44px, calc((100vw - 32px - 30px) / 7))',
        flex: '0 0 min(44px, calc((100vw - 32px - 30px) / 7))!important',
        marginTop: '4px',
        '@media (max-height: 580px) and (max-width: 360px)': {
            height: 'min(36px, calc((100vw - 32px - 30px) / 7))',
            width: 'min(36px, calc((100vw - 32px - 30px) / 7))',
            fontSize: '16px',
            flex: '0 0 min(36px, calc((100vw - 32px - 30px) / 7))!important',
        },
        '&:not(:nth-child(7n))': {
            marginRight: '5px',
        },
        '&.react-calendar__month-view__days__day--neighboringMonth': {
            visibility: 'hidden',
        },
        '&.react-calendar__tile--active, &.react-calendar__tile--rangeStart, &.react-calendar__tile--rangeEnd': {
            backgroundColor: theme.palette.additionalPrimary.main,
            color: '#FFFFFF',
            borderRadius: '50%',
        },
        '&.react-calendar__tile--range': {
            position: 'relative',
            overflow: 'visible!important',
        },
        '&.react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(.react-calendar__tile--rangeEnd)': {
            backgroundColor: 'transparent',
            color: '#081F32',
            borderRadius: 0,
        },
        '&.react-calendar__tile--range::after': {
            content: '""',
            position: 'absolute',
            backgroundColor: '#3F79B51F',
            top: 2,
            left: 0,
            height: 'calc(100% - 4px)',
            width: 'calc(100% + 5px)',
        },
        '&.react-calendar__tile--rangeStart.react-calendar__tile--rangeEnd::after,': {
            content: 'none',
        },
        '&.react-calendar__tile--rangeStart::after': {
            borderRadius: '50% 0 0 50%',
        },
        '&.react-calendar__tile--rangeEnd::after': {
            borderRadius: '0 50% 50% 0',
            width: '100%',
        }
    },
    tile: {
        fontSize: "20px",
        fontWeight: 600,
        lineHeight: "25px",
        letterSpacing: "0.002em",
        color: '#081F32',
        cursor: "pointer",
        flex: "0 0 50% !important",
        margin: "0",
        padding: "12px !important",
    },
    bottomSheet: {
        '@media (min-height: 580px)': {
            minHeight: '567px',
        },
        "@media (min-width: 600px)": {
            maxWidth: '375px',
        }
    }
}))


export const DateField = React.forwardRef<HTMLDivElement, DateFieldProps>((props, ref) => {
    const {value, onChange, range, disabled, maxDate, ...other} = props;
    const classes = useStyles(props);
    const $input = useRef<HTMLInputElement>();
    const [selectedDate, setSelectedDate] = React.useState<Date | [Date, Date]>(null);
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
    const [isPickerOpen, setIsPickerOpen] = useBooleanHistoryState();
    const [view, setView] = useState<CalendarProps["view"]>("month");
    const defaultActiveStartDate = React.useMemo(() => {
        if (selectedDate) {
            if (selectedDate instanceof Date) {
                return selectedDate;
            } else {
                return selectedDate[0];
            }
        }
        return maxDate;
    }, [selectedDate, maxDate]);
    const handleChange = (e) => {
        if (range) {
            const start = moment(e.target.value.split(" - ")[0], "YYYY-MM-DD");
            const end = moment(e.target.value.split(" - ")[1], "YYYY-MM-DD");
            if (start.isValid() && end.isValid()) {
                onChange && onChange([start.toDate(), end.toDate()]);
            } else {
                onChange && onChange(null);
            }
        } else {
            const value = moment(e.target.value, "YYYY-MM-DD");
            if (value.isValid()) {
                if (onChange) {
                    onChange(value.toDate());
                    handleChangeDate(value.toDate());
                }
            } else {
                onChange && onChange(null);
            }
        }
    }

    const handleChangeDate = (date: Date) => {
        if (range) {
            setSelectedDate(date);
        } else {
            setSelectedDate(date);
        }
    }

    const handleIconClick = e => {
        if (!disabled) setIsPickerOpen(true);
    }

    const handleConfirm = () => {
        if (range) {
            const rangeStart = moment(selectedDate[0]).format("YYYY-MM-DD");
            const rangeEnd = moment(selectedDate[1]).format("YYYY-MM-DD");
            if (rangeStart && rangeEnd) {
                const value = `${rangeStart} - ${rangeEnd}`;
                $input.current.value = value;
                handleChange({target: {value}});
                setIsPickerOpen(false)
            }
        } else {
            const value = moment(selectedDate as Date).format("YYYY-MM-DD");
            if (value) {
                $input.current.value = value;
                handleChange({target: {value}});
                setIsPickerOpen(false)
            }
        }

    }

    const handleCancel = () => {
        setSelectedDate(null);
        $input.current.value = null;
        handleChange({target: {value: null}});
        setIsPickerOpen(false);
    }

    const calendarValue = useMemo(() => {
        if (selectedDate) {
            if (range) {
                return selectedDate;
            } else {
                return moment(selectedDate as Date).format("YYYY-MM-DD");
            }
        } else if (value) {
            if (!range) {
                return moment(value as Date).format("YYYY-MM-DD");
            } else {
                return [moment(value[0] as Date).format("YYYY-MM-DD"), moment(value[1] as Date).format("YYYY-MM-DD")] as unknown as [Date, Date];
            }
        } else {
            return "";
        }
    }, [selectedDate, value, range]);

    const handleChangeView = useCallback(({view}) => {
        setView(view);
    }, []);

    useEffect(() => {
        if (isPickerOpen) {
            setIsCalendarOpen(true);
        } else {
            setTimeout(() => {
                setIsCalendarOpen(false);
            }, 300)
            setView("month");
        }
    }, [isPickerOpen]);

    return (
        <>
            <BottomSheet
                open={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                title="Выбрать дату"
                containerClassName={classes.bottomSheet}
                isCalendar
            >
                {isCalendarOpen && <Box display="flex" flexDirection="column" alignItems="center" paddingBottom={2} height="100%">
                    <Box className={classes.container}>
                        <Calendar
                            inputRef={ref}
                            value={calendarValue}
                            onChange={handleChangeDate}
                            className={classes.calendar}
                            tileClassName={{
                                "month": classes.tileMonth,
                            }[view] || classes.tile}
                            prevLabel={<LinaArrowRight color="additionalSecondary"/>}
                            nextLabel={<LinaArrowRight color="additionalSecondary"/>}
                            navigationLabel={({date, view, label}) => {
                                if (view == "month") {
                                    return moment(date).format("MMMM YYYY");
                                } else if (view == "year") {
                                    return moment(date).format("YYYY");
                                } else if (view == "decade") {
                                    const startDate = moment(date);
                                    const endDate = startDate.clone().add(10, "year");
                                    return `${startDate.format("YYYY")} - ${endDate.format("YYYY")}`;
                                } else if (view == "century") {
                                    const startDate = moment(date);
                                    const endDate = startDate.clone().add(100, "year");
                                    return `${startDate.format("YYYY")} - ${endDate.format("YYYY")}`;
                                }
                            }}
                            selectRange={range}
                            view={view}
                            minDetail="decade"
                            maxDetail="month"
                            maxDate={maxDate}
                            defaultActiveStartDate={defaultActiveStartDate}
                            onViewChange={handleChangeView}
                        />
                        <Box sx={{ marginTop: 'auto', padding: '0 16px'}}>
                            <Button
                                onClick={handleConfirm}
                                variant="contained"
                                color="additionalPrimary"
                                fullWidth
                                sx={{ marginBottom: 2 }}
                                disabled={!selectedDate}
                            >
                                Применить
                            </Button>
                            <Button
                                onClick={handleCancel}
                                variant="contained"
                                color="secondary"
                                fullWidth
                                disabled={!selectedDate}
                            >
                                Сбросить
                            </Button>
                        </Box>
                    </Box>
                </Box>}
            </BottomSheet>
            <TextField
                {...other as TextFieldProps}
                className={[classes.root, other?.className].filter(Boolean).join(" ")}
                ref={ref}
                InputProps={{
                    placeholder: "Выбрать",
                    ...props.InputProps,
                    readOnly: range,
                    inputRef: $input,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton disabled={props?.disabled} onClick={handleIconClick} sx={{padding: '10px'}}>
                                <CalendarIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                    onClick: (e) => e.preventDefault(),
                }}
                type={range ? "text" : "date"}
                onChange={handleChange}
                onClick={() => {
                    if (!disabled) {
                        handleIconClick(null);
                    }
                }}
                disabled={disabled}
                value={value
                    ? range
                        ? `${moment(value[0]).format("D.MM.YY")} - ${moment(value[1]).format("D.MM.YY")}`
                        : moment(value).format("YYYY-MM-DD")
                    : ""
                }
            />
        </>
    )
})
